import type { BoxProps } from "@mui/material";
import { Box, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { useAtomValue } from "jotai";
import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { imageRefineAtomsAtom } from "../ai-refine/store";
import { imagePreviewDimensions } from "../generate/store";
import type { LoadableTemplateImage } from "../generic/atoms/types/template";
import { TemplateImageAttribution } from "./template-image-attribution";

export const TemplateImage: FC<
  BoxProps & {
    img?: LoadableTemplateImage;
    designHuddle?: boolean;
    loadingComponent?: ReactNode;
  }
> = ({ img, designHuddle = false, sx = [], loadingComponent, ...props }) => {
  const [imageUrl, setImageUrl] = useState("none");

  const refineAtoms = useAtomValue(imageRefineAtomsAtom);

  const dimensions = useTemplateImageDimensions();

  const canRefine = refineAtoms && img?.state === "hasData" && img.data;

  useEffect(() => {
    const vtpUrl =
      img?.state === "hasData" && img.data?.url.state === "hasData" ? img.data.url.data : null;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    vtpUrl && setImageUrl(`url(${vtpUrl})`);
  }, [img]);

  return (
    <Box
      data-testid="template-image"
      sx={[
        {
          position: "relative",
          borderRadius: "5px",
          border: "5px solid #eee",
          padding: "5px",
          ...((canRefine || designHuddle) && {
            ":hover": {
              border: "1px dashed #00000066",
              padding: "9px",
              ".MuiButton-root": {
                display: "inline-flex",
              },
            },
          }),
          overflow: "hidden",
          // add 20: 2 * (border + padding)
          width: dimensions.width + 20,
          height: dimensions.height + 20,
        },
        ...(sx instanceof Array ? sx : [sx]),
      ]}
      {...props}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          boxSizing: "content-box",
          position: "relative",
          backgroundImage: imageUrl,
          backgroundSize: "cover",
        }}
        data-testid="template-image-bg"
      >
        {(img?.state !== "hasData" || img.data?.url.state !== "hasData") &&
          (loadingComponent || (
            <Skeleton sx={{ width: "100%", height: "100%" }} variant="rectangular" />
          ))}
        {img?.state === "hasData" &&
          img?.data?.unsplashExtra &&
          img.data.unsplashExtra.attributionData && (
            <TemplateImageAttribution attributionData={img.data.unsplashExtra.attributionData} />
          )}
      </Box>
    </Box>
  );
};

export const useTemplateImageDimensions = (): { width: number; height: number } => {
  const { image_type } = useAtomValue(imagePreviewDimensions);

  const theme = useTheme();

  const isGreaterThanXl = useMediaQuery(theme.breakpoints.up("xl"));

  const isLessThanLg = useMediaQuery(theme.breakpoints.down("lg"));

  if (image_type === "banner") return { width: 600, height: 200 };

  if (isGreaterThanXl) return { width: 400, height: 400 };

  if (isLessThanLg) return { width: 200, height: 200 };

  return { width: 250, height: 250 };
};
